import {useMutation} from '@apollo/client'
import {useIntl} from 'react-intl'
import {DEDUCTION_TYPE_MESSAGES as M} from '../deductionTypeMessages'
import {translatedDeductionTypes} from '../../../helpers/catalogMessages'
import React, {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  ADD_USER_DEDUCTION_TYPE,
  EDIT_USER_DEDUCTION_TYPE,
} from '../../../gql/mutations/userDeductionType'
import {
  IAllCommisionDeductionTypesNode,
  IResCreateCommisionDeductionTypes,
  IResUpdateCommisionDeductionTypes,
} from '../../../types/users'
import {parseId} from '../../../helpers'
import {DEDUCTION_TYPE_OPTIONS} from '../../../helpers/consts'

interface IModalAddUserDeductionTypeProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  isEdit?: boolean
  commisionDeductionType?: IAllCommisionDeductionTypesNode | undefined
}

export const ModalAddUserDeductionType: React.FC<IModalAddUserDeductionTypeProps> = ({
  keyboard,
  backdrop,
  isOpen,
  onClose,
  refetch,
  isEdit = false,
  commisionDeductionType,
}) => {
  const intl = useIntl()
  // const [gqlUpdate, {data, loading, error}] = useMutation<IResAddTag>(ADD_USER_DEDUCTION_TYPE)

  const [gqlCreate, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResCreateCommisionDeductionTypes>(ADD_USER_DEDUCTION_TYPE)

  const [gqlUpdate, {data: dataUpdate}] =
    useMutation<IResUpdateCommisionDeductionTypes>(EDIT_USER_DEDUCTION_TYPE)

  useEffect(() => {
    if (errorCreate)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: errorCreate.message}))
  }, [errorCreate])

  useEffect(() => {
    if (!dataCreate) return

    if (
      dataCreate.CommissionDeductionTypeCreate.success &&
      dataCreate.CommissionDeductionTypeCreate.commissionDeductionType?.id
    ) {
      toast.success(intl.formatMessage(M.toastSaved))
      onClose()
      refetch()
    } else if (
      dataCreate.CommissionDeductionTypeCreate.errors &&
      dataCreate.CommissionDeductionTypeCreate.errors.length > 0
    ) {
      toast.error(dataCreate.CommissionDeductionTypeCreate.errors[0]?.message || intl.formatMessage(M.createFailed))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate.CommissionDeductionTypeUpdate.success) {
      toast.success(intl.formatMessage(M.toastSaved))
      onClose()
      refetch()
    } else if (
      dataUpdate.CommissionDeductionTypeUpdate.errors &&
      dataUpdate.CommissionDeductionTypeUpdate.errors.length > 0
    ) {
      toast.error(dataUpdate.CommissionDeductionTypeUpdate.errors[0]?.message || intl.formatMessage(M.updateFailed))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const {name = '', deductionType = '', value = ''} = commisionDeductionType || {}

  const [formData, setFormData] = useState({
    name: isEdit ? name : '',
    deductionType: isEdit ? deductionType : '',
    value: isEdit ? value : '',
  })

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (commisionDeductionType && commisionDeductionType.id) {
      gqlUpdate({
        variables: {
          id: parseId(commisionDeductionType.id),
          input: {
            name: formData.name,
            deductionType: formData.deductionType || null,
            value: '' + parseFloat(formData.value),
          },
        },
      })
    } else {
      gqlCreate({
        variables: {
          input: {
            name: formData.name,
            deductionType: '' + formData.deductionType || null,
            value: '' + parseFloat(formData.value),
          },
        },
      })
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(isEdit ? M.modalEdit : M.modalAdd)}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>{intl.formatMessage(M.name)}</Form.Label>
            <Form.Control
              type='text'
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group className='mb-3' controlId='inputcolor'>
            <Form.Label>{intl.formatMessage(M.type)}</Form.Label>
            <Form.Select
              name='deductionType'
              value={formData.deductionType}
              onChange={(e) => handleChange('deductionType', e.target.value)}
              required
            >
              {translatedDeductionTypes(intl).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className='mb-3' controlId='exampleForm.ControlInput1'>
            <Form.Label>{intl.formatMessage(M.value)}</Form.Label>
            <Form.Control
              type='number'
              value={formData.value}
              onChange={(e) => handleChange('value', e.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            {intl.formatMessage(M.cancel)}
          </Button>
          <Button
            type='submit'
            variant='primary'
            className='d-flex align-items-center'
            disabled={loadingCreate}
          >
            {loadingCreate && <Spinner animation='border' size='sm' />}
            {intl.formatMessage(M.saveChanges)}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
